import React from 'react';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import churchForms from 'src/services/api/churchForms';
import { getComunidadNetworkColumnDefs } from 'src/containers/comunidad-network/admin/comunidadNetworkColumns';
import { Select, Input, Button } from 'semantic-ui-react';
import EditComunidadEmpresaModal from 'src/containers/comunidad-network/edit/EditComunidadEmpresaModal';
import { selectArea } from 'src/components/helpers';
import AGTable from 'src/components/table/aggrid';
import {
  AdminTableNetworkSection,
  AdminTableContent,
} from 'src/assets/styles/components/AdminTable';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';

const PER_PAGE = 50;

const ComunidadNetworkAdmin = () => {
  const [loading, set_loading] = React.useState(false);
  const [totalRecords, set_totalRecords] = React.useState(0);
  const [reviewCounts, setReviewCounts] = React.useState(null);
  const [showComunidadNetwork, set_showComunidadNetwork] = React.useState(null);
  const [selectedNetwork, set_selectedNetwork] = React.useState(null);
  const [inputs, setInputs] = React.useState({});
  const getCaptchaToken = useRecaptchaToken('all_comunidad_network');

  const gridApiRef = React.useRef(null);
  const filtersRef = React.useRef({});

  const makeDatasource = () => ({
    getRows: async (params) => {
      set_loading(true);
      const perPage = params.endRow - params.startRow || PER_PAGE;
      const currentPage = Math.floor(params.startRow / perPage) + 1;

      const captchaRequired = isCaptchaRequired();
      const captcha = await getCaptchaToken({ skip: !captchaRequired });
      if (captchaRequired && !captcha) {
        set_loading(false);
        set_totalRecords(0);
        params.successCallback([], 0);
        Swal.fire(RECAPTCHA_SECURITY_ERROR);
        return;
      }

      churchForms
        .getComunidadNetwork({
          ...filtersRef.current,
          currentPage,
          perPage,
          ...(captchaRequired ? { captcha } : {}),
        })
        .then((response) => {
          set_loading(false);
          if (response.data.success) {
            const rows = response.data.network || [];
            const total =
              typeof response.data.total === 'number'
                ? response.data.total
                : params.startRow + rows.length;
            set_totalRecords(total);
            if (response.data.reviewCounts) setReviewCounts(response.data.reviewCounts);
            params.successCallback(rows, total);
          } else {
            set_totalRecords(0);
            params.successCallback([], 0);
          }
        })
        .catch((error) => {
          set_loading(false);
          if (error.response?.status === 404) {
            set_totalRecords(0);
            params.successCallback([], 0);
          } else {
            if (error.response) Swal.fire(error.response.data.message);
            params.failCallback();
          }
        });
    },
  });

  const refreshGrid = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setDatasource(makeDatasource());
    }
  };

  const applyFilters = () => {
    filtersRef.current = {
      keyword: inputs.keyword || '',
      personKeyword: inputs.personKeyword || '',
      published: inputs.published,
    };
    refreshGrid();
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    filtersRef.current = {
      keyword: inputs.keyword || '',
      personKeyword: inputs.personKeyword || '',
      published: inputs.published,
    };
    params.api.setDatasource(makeDatasource());
  };

  const onEnter = (e) => {
    if (e.key === 'Enter') applyFilters();
  };

  const selectHandle = (a, { value, name }) => {
    setInputs({ ...inputs, [name]: value });
  };

  const handleInputs = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    setInputs({ ...inputs, [name]: value });
  };

  const onView = (network) => {
    set_selectedNetwork(network);
    set_showComunidadNetwork(network.id);
  };

  const closeModal = () => {
    set_showComunidadNetwork(null);
    set_selectedNetwork(null);
  };

  const onSaved = () => {
    closeModal();
    refreshGrid();
  };

  const columnDefs = React.useMemo(
    () =>
      getComunidadNetworkColumnDefs({
        onView,
        onDeleted: refreshGrid,
      }),
    []
  );

  return (
    <AdminTableNetworkSection>
      <AdminTableContent>
        <Button primary onClick={applyFilters}>
          Buscar
        </Button>
        <h2>Directorio de Empresas, Vendedores y Servicios</h2>
        <div className="options">
          <div className="search-query">
            <p>Buscar</p>
            <Input
              loading={loading}
              name={'keyword'}
              onChange={handleInputs}
              onKeyPress={onEnter}
              value={inputs.keyword || ''}
              placeholder="Buscar por empresa..."
            />
            <Input
              loading={loading}
              name={'personKeyword'}
              onKeyPress={onEnter}
              onChange={handleInputs}
              value={inputs.personKeyword || ''}
              placeholder="Buscar por propietario..."
            />
          </div>
          <div className="published">
            <p>Publicado</p>
            {selectArea({
              Select,
              options: ['No', 'Si'],
              label: 'Filtrar por publicado',
              name: 'published',
              value: inputs.published,
              loading,
              handler: selectHandle,
            })}
          </div>
        </div>
      </AdminTableContent>

      <div>
        <p>
          <b>Total de registros:</b> {totalRecords}
        </p>
        {reviewCounts && (
          <p>
            Pendientes por publicar: {reviewCounts.unpublished} <br />
            Con cambios por revisar: {reviewCounts.pendingChanges}
          </p>
        )}
        <AGTable
          columnDefs={columnDefs}
          rowModelType="infinite"
          onGridReady={onGridReady}
          pagination
          paginationPageSize={PER_PAGE}
          cacheBlockSize={PER_PAGE}
          rowHeight={60}
          suppressDragLeaveHidesColumns={false}
          defaultCol={{ sortable: false, filter: false, floatingFilter: false }}
        />
      </div>

      {showComunidadNetwork && selectedNetwork && (
        <EditComunidadEmpresaModal
          inputs={selectedNetwork}
          set_showComunidadNetwork={closeModal}
          onSaved={onSaved}
        />
      )}
    </AdminTableNetworkSection>
  );
};

export default ComunidadNetworkAdmin;
